import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import Modal from "@/components/Modal";
import CheckInbox from "@/components/CheckInbox";
import { Button, inputClass } from "@/components/base";
import { api, errField, errMsg, track } from "@/lib/api";
import { getAttribution } from "@/lib/utm";
import { isAndroid, isInApp, radarUrl, systemBrowserLink } from "@/lib/inapp";
import { tic } from "@/lib/sound";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PENDING = "archijo-pending-signup";

export function savePending(radarId, terms = false) {
  try {
    localStorage.setItem(PENDING, JSON.stringify({ radar_id: radarId || null, attribution: getAttribution(), terms, at: Date.now() }));
  } catch {}
}

export function takePending() {
  try {
    const raw = localStorage.getItem(PENDING);
    localStorage.removeItem(PENDING);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const PROD_HOSTS = ["archijo.com", "www.archijo.com"];

export function startGoogle(radarId, cfg, terms = false) {
  savePending(radarId, terms);
  const q = radarId ? `?radar=${encodeURIComponent(radarId)}` : "";
  // Production: own Google client only (server decides); the Emergent URL comes from /config in preview only.
  if (PROD_HOSTS.includes(window.location.hostname) || cfg.google_auth !== "emergent" || !cfg.emergent_auth_url) {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/start${q}`;
    return;
  }
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/auth/google" + q;
  window.location.href = `${cfg.emergent_auth_url}/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export const GoogleG = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.2 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.9 6.6 29.2 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.9 6.6 29.2 4.5 24 4.5c-7.5 0-14 4.3-17.7 10.2z" />
    <path fill="#4CAF50" d="M24 43.5c5.1 0 9.8-2 13.3-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.1 2.4-5.2 0-9.6-3.1-11.3-7.6l-6.5 5C9.9 39.1 16.4 43.5 24 43.5z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.8l6.2 5.2C41 35.9 43.5 30.4 43.5 24c0-1.2-.1-2.3-.4-3.5z" />
  </svg>
);

function EmailForm({ radarId, profile, terms, onNeedTerms }) {
  const [f, setF] = useState({ prenom: "", email: "" });
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!f.prenom.trim()) errs.prenom = "Entre ton prénom.";
    if (!EMAIL_RE.test(f.email.trim())) errs.email = "Entre un email valide.";
    setErr(errs);
    if (!terms) onNeedTerms();
    if (Object.keys(errs).length || !terms) return;
    setBusy(true);
    track("method_chosen", { method: "email" });
    try {
      const body = { prenom: f.prenom.trim(), email: f.email.trim(), radar_id: radarId || null, attribution: getAttribution(), terms: true };
      if (!radarId && profile?.commune) Object.assign(body, { metier: profile.metier, commune_code: profile.commune.code });
      const r = await api.post("/auth/signup", body);
      tic();
      setDone(r.data);
    } catch (e2) {
      const field = errField(e2);
      setErr(field === "email" || field === "prenom" ? { [field]: errMsg(e2, "") } : { form: errMsg(e2, "L'email n'est pas parti. Réessaie dans un instant.") });
    } finally {
      setBusy(false);
    }
  };
  if (done) return <CheckInbox email={f.email.trim()} wait={done.resend_in || 60} testPrefix="signup-inbox" />;
  const msg = (k) => err[k] && <p data-testid={`signup-error-${k}`} className="err-msg mt-1.5 text-[13px] text-ink">{err[k]}</p>;
  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-3" data-testid="signup-form">
      <div>
        <input className={inputClass} placeholder="Prénom" aria-label="Prénom" autoComplete="given-name" data-testid="signup-prenom" value={f.prenom} onChange={(e) => setF((s) => ({ ...s, prenom: e.target.value }))} />
        {msg("prenom")}
      </div>
      <div>
        <input className={inputClass} type="email" inputMode="email" placeholder="Email" aria-label="Email" autoComplete="email" data-testid="signup-email" value={f.email} onChange={(e) => setF((s) => ({ ...s, email: e.target.value }))} />
        {msg("email")}
      </div>
      {msg("form")}
      <Button type="submit" disabled={busy} data-testid="signup-submit" className="w-full">
        {busy ? "Envoi..." : "Recevoir mon code"}
      </Button>
    </form>
  );
}

function Terms({ checked, onChange, warn }) {
  return (
    <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-[14px] text-ink" data-testid="signup-terms">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5 shrink-0 accent-[var(--ink)]" data-testid="signup-terms-checkbox" />
      <span>
        J'accepte les <a href="/conditions" target="_blank" rel="noreferrer" className="underline underline-offset-4" data-testid="signup-terms-link">conditions d'utilisation</a>
        {warn && <span className="err-msg block text-[13px]" data-testid="signup-terms-error">Coche la case pour continuer.</span>}
      </span>
    </label>
  );
}

export default function SignupSheet({ open, onClose, radarId, profile }) {
  const [cfg, setCfg] = useState({ google_auth: "oauth", emergent_auth_url: null });
  const [email, setEmail] = useState(false);
  const [terms, setTerms] = useState(false);
  const [warn, setWarn] = useState(false);
  const inApp = isInApp();
  useEffect(() => {
    if (!open) return;
    track("sheet_opened", { radar_id: radarId || null });
    setEmail(inApp);
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
  }, [open, inApp]);
  const setT = (v) => { setTerms(v); if (v) setWarn(false); };

  const google = (
    <Button variant={inApp ? "outline" : "primary"} onClick={() => { if (!terms) return setWarn(true); track("method_chosen", { method: "google" }); startGoogle(radarId, cfg, true); }} data-testid="signup-google-btn" className="w-full gap-2.5">
      <GoogleG /> Continuer avec Google
    </Button>
  );
  const emailBlock = email ? (
    <EmailForm radarId={radarId} profile={profile} terms={terms} onNeedTerms={() => setWarn(true)} />
  ) : (
    <Button variant="outline" onClick={() => { track("method_chosen", { method: "email" }); setEmail(true); }} data-testid="signup-email-btn" className="w-full">
      Continuer avec mon email
    </Button>
  );
  const sep = (
    <div className="my-3 flex items-center gap-3 text-[13px] text-muted" data-testid="signup-or">
      <span className="h-px flex-1 bg-line" />ou<span className="h-px flex-1 bg-line" />
    </div>
  );
  const url = radarUrl(radarId, getAttribution());
  return (
    <Modal open={open} onClose={onClose} title={radarId ? "Débloque les 3 zones" : "Crée ton compte"} testId="signup-modal">
      {radarId && (
        <div className="-mt-1 mb-5 flex gap-2" data-testid="signup-locks">
          {[2, 3, 4].map((z) => (
            <span key={z} className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-line bg-surface-2 py-2 text-[13px] text-ink">
              <Lock className="h-3.5 w-3.5" strokeWidth={1.75} /> Zone {z}
            </span>
          ))}
        </div>
      )}
      {inApp ? (
        <>
          {emailBlock}
          <div className="mt-4 rounded-card border border-line px-4 py-3" data-testid="inapp-open">
            <a href={systemBrowserLink(url)} data-testid="inapp-open-link" data-url={url} className="text-[14px] font-medium text-ink underline underline-offset-4">
              {isAndroid() ? "Ouvrir dans Chrome" : "Ouvrir dans Safari"}
            </a>
            {!isAndroid() && <p className="mt-1 text-[12px] text-muted">Si rien ne se passe : touche ••• puis Ouvrir dans le navigateur.</p>}
          </div>
          {sep}
          {google}
        </>
      ) : (
        <>
          {google}
          {sep}
          {emailBlock}
        </>
      )}
      <div className="mt-3"><Terms checked={terms} onChange={setT} warn={warn} /></div>
    </Modal>
  );
}
